import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect } from "react";
import { useFragment } from "react-relay";

import { currentProfileAtom } from "../../../features/Club/atoms";
import { useCurrentSignUp } from "../components/useCurrentSignUp/useCurrentSignUp";
import { EventSignUpRegistration, EventSignUpRow } from "../types";
import { useInitEventSignUps_event$key } from "./__generated__/useInitEventSignUps_event.graphql";
import { eventSignUpsReducerAtom } from "./groupEvent";

export const useInitEventSignUps = ({
  fragmentRef,
}: {
  fragmentRef: useInitEventSignUps_event$key | null;
}) => {
  const data = useFragment(useInitEventSignUpsFragmentString, fragmentRef);

  const currentProfile = useAtomValue(currentProfileAtom);
  const dispatch = useSetAtom(eventSignUpsReducerAtom);

  const currentUserSignup = useCurrentSignUp(data ?? null);

  useEffect(() => {
    let currentEventSignup: EventSignUpRow;
    const supportsPublic = data?.eventSeries?.facility?.club?.supportsPublic;

    if (!currentProfile?.memberId && supportsPublic) {
      return;
    }

    if (currentUserSignup) {
      currentEventSignup = {
        id: currentUserSignup.id,
        waitlistIndex: currentUserSignup.waitlistIndex ?? null,
        checkedIn: currentUserSignup.checkedIn,
        registrations:
          currentUserSignup.registrations?.edges?.flatMap((e) =>
            e?.node?.id
              ? [
                  {
                    id: e.node.id,
                    index: e.node.index === undefined ? null : e.node.index,
                    type: e.node.type === undefined ? null : e.node.type,
                    payingMemberId:
                      e.node.payingMember?.id ??
                      e.node.payingCustomer?.id ??
                      e.node.registrant?.id ??
                      "",
                    primaryRegistrationChargeTypeId:
                      e.node.primaryChargeType?.id ?? null,
                    unknownRegistrantDisplayName:
                      e.node.unknownRegistrantDisplayName ?? null,
                    registrant: {
                      ...e.node.registrant,
                      playerId: e.node.registrant?.id,
                      email: e.node.registrant?.email,
                      phoneNumber:
                        e.node.registrant?.phoneNumber?.nationalNumber ?? null,
                      ghinNumber:
                        e.node.registrant?.departmentMemberProfiles?.find(
                          (profile) => !!profile?.ghinNumber
                        )?.ghinNumber ?? null,
                    },
                    formResponses: e.node.formResponses?.edges?.map((fr) => ({
                      questionConfigurationId:
                        fr?.node?.questionConfiguration.id ?? "",
                      stringValue: fr?.node?.stringValue,
                      booleanValue: fr?.node?.booleanValue,
                      transportationTypeId: fr?.node?.transportationType?.id,
                    })),
                  },
                ]
              : []
          ) ?? [],
        ownerClient: currentUserSignup.ownerClient
          ? {
              firstName: currentUserSignup.ownerClient.firstName ?? "",
              lastName: currentUserSignup.ownerClient.lastName ?? "",
              id: currentUserSignup.ownerClient.id ?? "",
              profileImagePublicId:
                currentUserSignup.ownerClient.profileImage?.publicId,
            }
          : undefined,
      };
    } else {
      if (!currentProfile?.memberId && supportsPublic) {
        return;
      } else if (!currentProfile?.memberId) {
        throw new Error("No member id found for current session");
      }

      // If member signup proxy is allowed, we do not put the member as the first registrant,
      // we will show them as the first person in the patrons list

      const registrations: EventSignUpRegistration[] =
        data?.memberSignUpProxyAllowed
          ? []
          : [
              {
                id: null,
                index: 0,
                type: supportsPublic ? "CUSTOMER" : "MEMBER",
                payingMemberId: currentProfile.memberId,
                primaryRegistrationChargeTypeId: null,
                registrant: {
                  playerId: currentProfile.memberId,
                  profileImage: currentProfile.profileImagePublicId
                    ? {
                        publicId: currentProfile.profileImagePublicId,
                      }
                    : undefined,
                  firstName: currentProfile.firstName,
                  lastName: currentProfile.lastName,
                  id: currentProfile.memberId ?? "",
                  email: currentProfile.email,
                  phoneNumber: currentProfile.phoneNumber ?? null,
                  ghinNumber: currentProfile.ghinNumber ?? null,
                },
              },
            ];

      currentEventSignup = {
        id: null,
        tempID: "",
        checkedIn: "NONE",
        waitlistIndex: null,
        registrations,
        isDirty: true,
        ownerClient: {
          firstName: currentProfile.firstName ?? "",
          lastName: currentProfile.lastName ?? "",
          id: currentProfile.memberId ?? "",
        },
      };
    }
    dispatch({ type: "initialize", payload: currentEventSignup });
  }, [
    currentProfile?.firstName,
    currentProfile?.lastName,
    currentProfile?.memberId,
    currentProfile?.profileImagePublicId,
    currentProfile?.email,
    currentProfile?.phoneNumber,
    currentProfile?.ghinNumber,
    currentUserSignup,
    dispatch,
    data?.memberSignUpProxyAllowed,
    data?.eventSeries?.facility?.club?.supportsPublic,
  ]);
};

export const useInitEventSignUpsFragmentString = graphql`
  fragment useInitEventSignUps_event on Event {
    memberSignUpProxyAllowed
    ...useCurrentSignUp_event
    signUpStatusForClient(clientId: $memberId)
    eventSeries {
      facility {
        club {
          supportsPublic
        }
      }
    }
  }
`;
